/**
 * La barre haute, commune à tous les écrans.
 *
 * Forme arrêtée par l'utilisateur le 03/09/2026, en trois passes :
 * « en haut de page STAFA sur la gauche avec le module actif », puis « le
 * sélectionneur de module fait top vieillot », puis « la barre de recherche tu
 * la mets sur la droite à côté de l'aide et en toute petite ». Maquette validée
 * le même jour.
 *
 * **Deux rangées, deux natures.** La rangée haute porte l'APPLICATION : quel
 * module, et les outils qui ne dépendent d'aucun module (recherche générale,
 * aide, société). La rangée basse porte l'ÉCRAN : les entrées du module
 * courant. Un module sans sous-menu ne dessine pas de deuxième rangée.
 *
 * **Paramètres est dans la liste des modules, pas dans le rond.** Il y figurait
 * aux deux endroits dans une version intermédiaire ; l'utilisateur l'a relevé.
 * La ligne de partage : le rond porte la PERSONNE (mon compte, se déconnecter),
 * la liste porte l'APPLICATION — et Paramètres est un module (import,
 * sauvegarde, personnes et droits, journal des actions — cadrage §6).
 *
 * **Ce qui est dessiné mais inerte, et pourquoi c'est visible.** L'aide, la
 * recherche générale et « Mon compte » n'ont pas d'écran. Ils sont posés
 * DÉSACTIVÉS plutôt que retirés : les retirer ferait diverger le code de la
 * maquette validée, et les laisser cliquables ferait promettre à la barre ce
 * qu'elle ne tient pas. Même traitement que les modules non construits.
 */

import { Fragment, useEffect, useRef, useState } from 'react';
import { initiales, type Module, type SousMenu } from './modules.js';

/** Ce qui est ouvert sous la barre. Un seul à la fois. */
type Deroule = 'aucun' | 'modules' | 'recherche' | 'compte';

export interface ProprietesBarreHaute {
  readonly modules: readonly Module[];
  /** Clé du module courant. */
  readonly module: string;
  readonly quandModule: (cle: string) => void;
  /** Les entrées du module courant. Vide : pas de deuxième rangée. */
  readonly sousMenu: readonly SousMenu[];
  readonly sousMenuCourant: string;
  readonly quandSousMenu: (cle: string) => void;
  readonly societe: string;
  readonly utilisateur: { readonly nom: string; readonly role: string };
  readonly quandDeconnexion: () => void;
}

export function BarreHaute({
  modules,
  module,
  quandModule,
  sousMenu,
  sousMenuCourant,
  quandSousMenu,
  societe,
  utilisateur,
  quandDeconnexion,
}: ProprietesBarreHaute) {
  const [deroule, setDeroule] = useState<Deroule>('aucun');
  const barre = useRef<HTMLDivElement>(null);

  // Un menu ouvert se referme au clic ailleurs et à Échap. Sans cela il faut
  // recliquer le bouton exactement, ce que personne ne fait : on clique dans la
  // page et le menu reste ouvert par-dessus.
  useEffect(() => {
    if (deroule === 'aucun') return;

    const auClic = (evenement: MouseEvent) => {
      const cible = evenement.target;
      if (cible instanceof Node && barre.current?.contains(cible)) return;
      setDeroule('aucun');
    };
    const aLaTouche = (evenement: KeyboardEvent) => {
      if (evenement.key === 'Escape') setDeroule('aucun');
    };

    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', aLaTouche);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', aLaTouche);
    };
  }, [deroule]);

  const courant = modules.find((m) => m.cle === module);
  const marque = initiales(societe);

  const basculer = (vers: Deroule) =>
    setDeroule((actuel) => (actuel === vers ? 'aucun' : vers));

  return (
    <div className="cadre-barre" ref={barre}>
      <div className="barre-haute">
        <div className="marque-appli">
          <span className="mot-appli">STAFA</span>
          <div className="filet" />
          <div className="cadre-menu">
            <button
              className="choix-module"
              type="button"
              aria-haspopup="menu"
              aria-expanded={deroule === 'modules'}
              onClick={() => basculer('modules')}
            >
              {courant?.nom ?? 'Stafa'}
              <Chevron />
            </button>
            {deroule === 'modules' && (
              <div className="menu-deroulant" role="menu">
                {modules.map((m) => (
                  <Fragment key={m.cle}>
                    {m.part === true && <div className="separateur" />}
                    {m.ouvert ? (
                      <button
                        className={m.cle === module ? 'courant' : undefined}
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          quandModule(m.cle);
                          setDeroule('aucun');
                        }}
                      >
                        {m.nom}
                      </button>
                    ) : (
                      <span className="a-venir">{m.nom}</span>
                    )}
                  </Fragment>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="outils-droite">
          <div className="cadre-menu">
            <button
              className="bouton-rond"
              type="button"
              aria-label="Rechercher partout"
              aria-haspopup="dialog"
              aria-expanded={deroule === 'recherche'}
              disabled
              title="À venir"
              onClick={() => basculer('recherche')}
            >
              <Loupe taille={17} />
            </button>
            {deroule === 'recherche' && (
              <div className="menu-deroulant a-droite large">
                <div className="champ-recherche">
                  <Loupe taille={15} />
                  <input
                    type="search"
                    aria-label="Recherche générale"
                    placeholder="Client, transporteur, site, immatriculation…"
                  />
                </div>
              </div>
            )}
          </div>

          <button
            className="bouton-rond"
            type="button"
            aria-label="Aide"
            disabled
            title="À venir"
          >
            <PointInterrogation />
          </button>

          <div className="cadre-menu">
            <button
              className="rond-societe"
              type="button"
              aria-haspopup="menu"
              aria-expanded={deroule === 'compte'}
              aria-label={`${societe} — compte`}
              onClick={() => basculer('compte')}
            >
              {marque}
            </button>
            {deroule === 'compte' && (
              <div className="menu-deroulant a-droite" role="menu">
                <div className="entete">{societe}</div>
                <div className="sous-titre">
                  {utilisateur.nom} · {utilisateur.role}
                </div>
                <div className="separateur" />
                <span className="a-venir">Mon compte</span>
                <button type="button" role="menuitem" onClick={quandDeconnexion}>
                  Se déconnecter
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {sousMenu.length > 0 && (
        <div className="sous-navigation" role="tablist">
          {sousMenu.map((entree) => (
            <button
              className="onglet-sous"
              type="button"
              role="tab"
              key={entree.cle}
              aria-selected={entree.cle === sousMenuCourant}
              onClick={() => quandSousMenu(entree.cle)}
            >
              {entree.nom}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Les dessins
//
// Ils sont écrits ici plutôt que chargés : trois traits chacun, et une
// dépendance de moins. `aria-hidden` sur tous — ce qu'ils veulent dire est
// déjà dans l'`aria-label` du bouton qui les porte, et un lecteur d'écran qui
// annoncerait les deux dirait la chose deux fois.
// ---------------------------------------------------------------------------

function Chevron() {
  return (
    <svg width="11" height="7" viewBox="0 0 11 7" fill="none" aria-hidden="true">
      <path
        d="M1 1.5 5.5 5.5 10 1.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Loupe({ taille }: { readonly taille: number }) {
  // Le dessin est décrit dans une grille de 17 et redimensionné : un seul
  // tracé pour les deux tailles, plutôt que deux tracés à garder d'accord.
  return (
    <svg
      width={taille}
      height={taille}
      viewBox="0 0 17 17"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="7.4" cy="7.4" r="5.4" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M11.6 11.6 15.2 15.2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PointInterrogation() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <circle cx="9" cy="9" r="7.1" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M6.9 6.7a2.1 2.1 0 1 1 2.6 2.05c-.4.11-.6.42-.6.83v.55"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <circle cx="9" cy="12.6" r="0.85" fill="currentColor" />
    </svg>
  );
}
